import React, {createContext, useState, useContext } from "react";
import { UserAPIService } from "./UserAPIService";
import { AuthUserLoginResponse } from "../models/AuthUserLoginResponse";
import { AuthAPIService } from "./AuthAPIServices";

interface AuthContextType {
    token: string | null;
    isAdmin: string;
    isSuperAdmin: string;
    showMessages: string;
    userId: string;
    userName: string;
    emailAddress: string;
    forename: string;
    surname: string;
    empNo: string,
    ppsNumber: string,
    pensionScheme: string,
    login: (token: AuthUserLoginResponse) => void;
    logout: () => void;
    getUserDetails: (token: string | null) => void;
    userDetailsLoaded: boolean;
    checkTokenExpiry: () => void;
    messagesDisplayed: () => void;
}

// Create the context
const AuthContext = createContext<AuthContextType>({
    token: null,
    isAdmin: 'false',
    isSuperAdmin: 'false',
    showMessages: 'true',
    userId: '',
    userName: '',
    emailAddress: '',
    forename: '',
    surname: '',
    empNo: '',
    ppsNumber: '',
    pensionScheme: '',
    login: () => {},
    logout: () => {},
    getUserDetails: () => {},
    userDetailsLoaded: false,
    checkTokenExpiry: () => {},
    messagesDisplayed: () => {}
});

// Create the the provider component
export const AuthProvider = ({ children }: any) => {
    const [token, setToken] = useState(localStorage.getItem('token') || null);
    const [tokenExpiry, setTokenExpiry] = useState(localStorage.getItem('tokenExpiry') || null);
    const [userDetailsLoaded, setUserDetailsLoaded] = useState(false);
    const [isAdmin, setIsAdmin] = useState('false');
    const [isSuperAdmin, setIsSuperAdmin] = useState('false');
    const [showMessages, setShowMessages] = useState(localStorage.getItem('showMessages') || 'true');
    const [userId, setUserId] = useState('true');
    const [userName, setUserName] = useState<string>('');
    const [emailAddress, setEmailAddress] = useState<string>('');
    const [forename, setForename] = useState<string>(localStorage.getItem('forename') || '');
    const [surname, setSurname] = useState<string>(localStorage.getItem('surname') || '');
    const [empNo, setEmpNo] = useState<string>('');
    const [ppsNumber, setPPSNumber] = useState<string>(localStorage.getItem('ppsNumber') || '');
    const [pensionScheme, setPensionScheme] = useState<string>(localStorage.getItem('pensionScheme') || '');
    const userApiService = new UserAPIService();
    const authApiService = new AuthAPIService();

    const getUserDetails = async (newToken: string | null) => {
        userApiService.getUserDetails(newToken).then((response) => {
            if (response.error) {
                // reset info
                setUserDetailsLoaded(true);
            }
            else {
                const userInfo = response.response?.userInfo;
                if (userInfo) {
                    const firstAccount = userInfo.userAccounts.find((account, index) => index === 0);
                    setIsAdmin(userInfo.isAdmin.toLocaleString());
                    setIsSuperAdmin(userInfo.superAdmin.toLocaleString());
                    setUserId(userInfo.userId.toLocaleString());
                    setUserName(userInfo.userName);
                    setEmailAddress(userInfo.emailAddress);
                    setForename(userInfo.forename);
                    setSurname(userInfo.surname);
                    setEmpNo(firstAccount && firstAccount.empNo ? firstAccount.empNo : '');
                    setPPSNumber(firstAccount && firstAccount.ppsNumber ? firstAccount.ppsNumber : '');
                    setPensionScheme(firstAccount && firstAccount.pensionScheme ? firstAccount.pensionScheme : '');
                    localStorage.setItem('sendPaperPaySlip', (userInfo.sendPaperPayslip || userInfo.requestedPaperPayslip) ? 'true' : 'false');
                    localStorage.setItem('ppsNumber', firstAccount && firstAccount.ppsNumber ? firstAccount.ppsNumber : '');
                    localStorage.setItem('pensionScheme', firstAccount && firstAccount.pensionScheme ? firstAccount.pensionScheme : '');
                    localStorage.setItem('forename', userInfo.forename);
                    localStorage.setItem('surname', userInfo.surname);
                }
                setUserDetailsLoaded(true);
            }
        })
    }

    const refreshToken = () => {
        authApiService.refreshToken(token ? token : '').then((response) => {
            if (response.response && response.response?.token) {
                setToken(response.response?.token);
                setTokenExpiry(response.response.tokenExipiry);
                localStorage.setItem('token', response.response.token);
                localStorage.setItem('tokenExpiry', response.response.tokenExipiry);
            } else {
                logout();
            }
        });
    }

    const authLogout = async () => {
        authApiService.logout(token ? token : '').then((response) => {
            if (response.response) {
                // Either way we will be login the user out.
            }
        })
    }

    const login = (response: AuthUserLoginResponse) => {
        setToken(response.token);
        setTokenExpiry(response.tokenExipiry);
        setIsAdmin(response.isAdmin.toString());
        setShowMessages(showMessages);
        localStorage.setItem('token', response.token);
        localStorage.setItem('tokenExpiry', response.tokenExipiry);
        localStorage.setItem('showMessages', showMessages);
        
        // Get User Details
        getUserDetails(response.token);
    }

    const logout = async () => {
        // Log the user out from API.
        await authLogout().then(() => 
        {
            setToken(null);
            setTokenExpiry(null);
            setIsAdmin('false');
            setIsSuperAdmin('false');
            setShowMessages('false');
            setUserId('');
            setUserName('');
            setEmailAddress('');
            setForename('');
            setSurname('');
            setEmpNo('');
            setPPSNumber('');
            setPensionScheme('');
            localStorage.removeItem('token');
            localStorage.removeItem('tokenExpiry');
            localStorage.removeItem('showMessages');
            localStorage.removeItem('forename');
            localStorage.removeItem('surname');
            localStorage.removeItem('sendPaperPaySlip');
            localStorage.removeItem('ppsNumber');
            localStorage.removeItem('pensionScheme');
        });

    }

    const checkTokenExpiry = () => {
        if (tokenExpiry) {
            const expiryDate = new Date(tokenExpiry);
            // Subtract 5 minutes (300,000 milliseconds) from the server date
            const expiryDateWithBuffer = new Date(expiryDate.getTime() - 300000);
            // Get the current date and time in UTC
            const currentDate = new Date();

            if (currentDate > expiryDateWithBuffer) {
                refreshToken();
            }
        }
    }

    const messagesDisplayed = () => {
        setShowMessages('false');
        localStorage.setItem('showMessages', 'false');
    }

    return (
        <AuthContext.Provider value={{ 
            token, 
            isAdmin, 
            isSuperAdmin, 
            showMessages, 
            userId, 
            userName, 
            emailAddress,
            forename,
            surname,
            empNo,
            ppsNumber,
            pensionScheme,
            login, 
            logout,
            getUserDetails,
            userDetailsLoaded, 
            checkTokenExpiry, 
            messagesDisplayed 
        }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => useContext(AuthContext);
